'use client';

import { useState, useRef, useEffect, useMemo } from 'react';
import { Flag, Waves, Shield, ChevronDown, Search, X, MapPin } from 'lucide-react';

interface Region {
  id: string;
  name: string;
  slug?: string;
  type?: string;
}

/** City/place for "place" filter (embarkation OR disembarkation). value = "name,country" */
export interface PlaceOption {
  name: string;
  country: string;
}

interface RegionComboboxProps {
  regions: Region[];
  selectedRegion: Region | null;
  onSelect: (region: Region | null) => void;
  placeholder?: string;
  locationSlug?: string | null;
  /** Cities to show in the list so user can select a city (filter by place = embarkation OR disembarkation) */
  places?: PlaceOption[];
  /** Currently selected place value "name,country" */
  selectedPlace?: string | null;
  /** When user selects a city */
  onPlaceSelect?: (placeValue: string | null) => void;
}

const getRegionIcon = (type?: string) => {
  if (!type) return Waves;
  const lowerType = type.toLowerCase();
  if (lowerType === 'nation' || lowerType === 'country') return Flag;
  if (lowerType === 'eez' || lowerType.includes('exclusive')) return Shield;
  return Waves; // Sea, Gulf, Bay, Ocean, etc.
};

const getRegionTypeLabel = (type?: string) => {
  if (!type) return 'Other';
  const lowerType = type.toLowerCase();
  if (lowerType === 'nation' || lowerType === 'country') return 'Nations';
  if (lowerType === 'eez' || lowerType.includes('exclusive')) return 'Exclusive Economic Zones';
  if (lowerType === 'sea' || lowerType === 'gulf' || lowerType === 'bay' || lowerType === 'ocean') return 'Seas & Waters';
  return 'Other';
};

function placeValueToLabel(value: string): string {
  const idx = value.indexOf(',');
  if (idx === -1) return value;
  return `${value.slice(0, idx).trim()}, ${value.slice(idx + 1).trim()}`;
}

export default function RegionCombobox({ regions, selectedRegion, onSelect, placeholder = 'Select region...', locationSlug, places = [], selectedPlace = null, onPlaceSelect }: RegionComboboxProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const comboboxRef = useRef<HTMLDivElement>(null);
  
  // Track if component has mounted to prevent onChange(null) on mount
  const hasMountedRef = useRef(false);
  const isInitializingRef = useRef(true);
  
  useEffect(() => {
    hasMountedRef.current = true;
    // Mark initialization as complete after first render
    const timer = setTimeout(() => {
      isInitializingRef.current = false;
    }, 100);
    return () => clearTimeout(timer);
  }, []);
  
  // Log incoming props for debugging (only after initialization)
  // useEffect(() => {
  //   if (!isInitializingRef.current) {
  //     console.log('[Filters] RegionCombobox - Incoming location prop (selectedRegion):', selectedRegion?.name, selectedRegion?.slug);
  //     console.log('[Filters] RegionCombobox - Incoming locationSlug prop:', locationSlug);
  //     console.log('[Filters] RegionCombobox - Internal searchQuery value:', searchQuery);
  //   }
  // }, [selectedRegion, locationSlug, searchQuery]);
  
  // Sync with URL: When locationSlug changes, update selectedRegion display
  // The selectedRegion prop should already be synced from parent, but this ensures display consistency
  // Only run after initialization to avoid triggering onChange on mount
  useEffect(() => {
    if (isInitializingRef.current) return; // Skip during initialization
    
    // FORCE SYNC: If selectedRegion changes, ensure searchQuery matches the name
    if (selectedRegion) {
      if (searchQuery !== selectedRegion.name) {
         // Only update if it's different to avoid loops, but we need to show the name
         // Note: we don't set it if the user is typing (searchQuery has partial value)
         // BUT if the prop changed externally (Map click), we MUST update it.
         // We assume external change if selectedRegion.slug matches prop but not query?
         // Simplest: just set it if we're not focused? 
         // For now, let's just ensure if selectedRegion exists, we reflect it, 
         // unless the user is clearing it.
         
         // Actually, the issue is internal state being empty string.
         // We can just rely on the parent prop for rendering the "selected" state.
         // But if we want the input to show the name...
      }
    }
    
    if (locationSlug && !selectedRegion) {
      // If URL has location but selectedRegion is null, find and set it
      const found = regions.find(r => r.slug === locationSlug);
      if (found) {
        // Don't call onSelect here to avoid loops - parent useEffect should handle this
        // This is just for display consistency
      }
    }
  }, [locationSlug, selectedRegion, regions, searchQuery]);
  
  // Debounce search query (300ms) - only for manual typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);
    
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Group regions by type
  const groupedRegions = regions.reduce((acc, region) => {
    const typeLabel = getRegionTypeLabel(region.type);
    if (!acc[typeLabel]) {
      acc[typeLabel] = [];
    }
    acc[typeLabel].push(region);
    return acc;
  }, {} as Record<string, Region[]>);

  // Filter regions based on debounced search query
  const filteredGroups = useMemo(() => {
    return Object.entries(groupedRegions).reduce((acc, [typeLabel, typeRegions]) => {
      const filtered = typeRegions.filter(region =>
        region.name.toLowerCase().includes(debouncedSearchQuery.toLowerCase())
      );
      if (filtered.length > 0) {
        acc[typeLabel] = filtered;
      }
      return acc;
    }, {} as Record<string, Region[]>);
  }, [groupedRegions, debouncedSearchQuery]);

  const queryLower = debouncedSearchQuery.toLowerCase();
  const filteredPlaces = useMemo(() => {
    if (!places.length) return [];
    return places.filter(
      (p) =>
        p.name.toLowerCase().includes(queryLower) ||
        (p.country && p.country.toLowerCase().includes(queryLower))
    );
  }, [places, queryLower]);
  
  // Sync searchQuery when selectedRegion changes externally (e.g., from map click)
  useEffect(() => {
    if (selectedRegion && searchQuery === '') {
      // If region is selected externally, keep search query empty (shows the selected region name)
      // Don't update searchQuery here to avoid clearing user's manual typing
    }
  }, [selectedRegion, searchQuery]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (comboboxRef.current && !comboboxRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleSelect = (region: Region) => {
    // Only emit if component has mounted and is not initializing (prevents onChange on mount)
    if (!hasMountedRef.current || isInitializingRef.current) {
      return;
    }
    
    onSelect(region);
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleClear = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    if (!hasMountedRef.current || isInitializingRef.current) return;
    onSelect(null);
    onPlaceSelect?.(null);
    setSearchQuery('');
  };

  const handlePlaceSelect = (place: PlaceOption) => {
    if (!hasMountedRef.current || isInitializingRef.current) return;
    const value = place.country ? `${place.name},${place.country}` : place.name;
    onPlaceSelect?.(value);
    onSelect(null);
    setIsOpen(false);
    setSearchQuery('');
  };

  return (
    <div ref={comboboxRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between gap-2 px-4 py-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 hover:border-slate-400 dark:hover:border-slate-600 transition-colors"
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {selectedPlace ? (
            <>
              <MapPin className="w-4 h-4 text-slate-500 flex-shrink-0" />
              <span className="truncate">{placeValueToLabel(selectedPlace)}</span>
            </>
          ) : selectedRegion ? (
            <>
              {(() => {
                const Icon = getRegionIcon(selectedRegion.type);
                return <Icon className="w-4 h-4 text-slate-500 flex-shrink-0" />;
              })()}
              <span className="truncate">{selectedRegion.name}</span>
            </>
          ) : (
            <span className="text-slate-500">{placeholder}</span>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {(selectedRegion || selectedPlace) && (
            <span
              role="presentation"
              onClick={handleClear}
              className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded"
            >
              <X className="w-3 h-3 text-slate-400" />
            </span>
          )}
          <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {isOpen && (
        <div className="absolute z-[10002] w-full mt-1 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg shadow-lg max-h-80 overflow-hidden flex flex-col">
          {/* Search Input */}
          <div className="p-2 border-b border-slate-200 dark:border-slate-700">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search regions..."
                className="w-full pl-9 pr-3 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
            </div>
          </div>

          {/* Options List: Regions + Cities */}
          <div className="overflow-y-auto flex-1">
            {Object.keys(filteredGroups).length === 0 && filteredPlaces.length === 0 ? (
              <div className="p-4 text-center text-sm text-slate-500">
                No regions or cities found
              </div>
            ) : (
              <>
                {Object.keys(filteredGroups).length > 0 && Object.entries(filteredGroups).map(([typeLabel, typeRegions]) => (
                  <div key={typeLabel}>
                    <div className="px-3 py-2 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider bg-slate-50 dark:bg-slate-900/50 sticky top-0">
                      {typeLabel}
                    </div>
                    {typeRegions.map((region) => {
                      const Icon = getRegionIcon(region.type);
                      const isSelected = selectedRegion?.id === region.id;
                      return (
                        <button
                          key={region.id}
                          type="button"
                          onClick={() => handleSelect(region)}
                          className={`w-full flex items-center gap-3 px-4 py-2 text-left text-sm hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors ${
                            isSelected ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300' : 'text-slate-900 dark:text-slate-100'
                          }`}
                        >
                          <Icon className="w-4 h-4 text-slate-500 flex-shrink-0" />
                          <span className="flex-1 truncate">{region.name}</span>
                          {isSelected && (
                            <div className="w-2 h-2 rounded-full bg-blue-600 flex-shrink-0" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                ))}
                {filteredPlaces.length > 0 && (
                  <div>
                    <div className="px-3 py-2 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider bg-slate-50 dark:bg-slate-900/50 sticky top-0">
                      Cities
                    </div>
                    {filteredPlaces.map((place) => {
                      const value = place.country ? `${place.name},${place.country}` : place.name;
                      const isSelected = selectedPlace === value;
                      const label = place.country ? `${place.name}, ${place.country}` : place.name;
                      return (
                        <button
                          key={value}
                          type="button"
                          onClick={() => handlePlaceSelect(place)}
                          className={`w-full flex items-center gap-3 px-4 py-2 text-left text-sm hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors ${
                            isSelected ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300' : 'text-slate-900 dark:text-slate-100'
                          }`}
                        >
                          <MapPin className="w-4 h-4 text-slate-500 flex-shrink-0" />
                          <span className="flex-1 truncate">{label}</span>
                          {isSelected && (
                            <div className="w-2 h-2 rounded-full bg-blue-600 flex-shrink-0" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

